/**
 * Self-serve onboarding client (ADR-0041, Fase 0).
 *
 * The ONE auth-free API surface — served on the reserved `app.<base>` host. No
 * token store (there's no session yet), a minimal fetch wrapper rooted at
 * `/api/onboarding`. Two calls: live slug availability + signup (which
 * provisions a tenant and hands back the subdomain to redirect into).
 */

import type { BillingPayload } from "@/lib/billingProfile";

const BASE = "/api/onboarding";

export class OnboardingApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string = "onboarding_error",
  ) {
    super(message);
    this.name = "OnboardingApiError";
  }
}

/** Parse the backend error envelope. Our HubError routes are `{error:{code,message}}`;
 * pydantic request-validation (422) is FastAPI's `{detail:[…]}` / `{detail:"…"}`. */
async function readError(resp: Response): Promise<OnboardingApiError> {
  let body: unknown = null;
  try {
    body = await resp.json();
  } catch {
    /* not JSON */
  }
  const b = body as { error?: { code?: string; message?: string }; detail?: unknown } | null;
  const hub = b?.error;
  if (hub?.message) return new OnboardingApiError(resp.status, hub.message, hub.code);
  const detail = b?.detail;
  if (typeof detail === "string") return new OnboardingApiError(resp.status, detail);
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] as { msg?: string };
    if (first?.msg) return new OnboardingApiError(resp.status, first.msg);
  }
  return new OnboardingApiError(resp.status, resp.statusText || "Request failed");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(BASE + path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!resp.ok) throw await readError(resp);
  const text = await resp.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** Availability verdict for a desired slug. `reason` is a stable backend code
 * (`tenancy:slug_exists` / `…_reserved:*` / `…_invalid:*` / …) the UI maps. */
export interface SlugCheck {
  available: boolean;
  reason: string | null;
}

/** The invoicing profile a purchase carries. The FORM shape, the completeness rule
 * and the form→wire serializer live in `lib/billingProfile` — ONE place for both
 * self-serve doors (signup and "Aggiungi workspace"); this is just the wire alias. */
export type OnboardingBilling = BillingPayload;

export interface SignupBody {
  company_name: string;
  slug: string;
  admin_email: string;
  admin_password: string;
  plan_key?: string | null;
  billing_period?: "month" | "year";
  billing?: OnboardingBilling | null;
}

/** The provisioned tenant's slug + where to send the visitor to log in. */
export interface SignupResult {
  slug: string;
  redirect_url: string;
}

/** A public plan offered at signup (mirrors the backend `OnboardingPlan`). */
export interface OnboardingPlan {
  key: string;
  name: string;
  description: string | null;
  price_cents: number | null;
  price_year_cents: number | null;
  currency: string;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
}

/** A workspace the identity may enter (the picker feed). */
export interface IdentityWorkspace {
  slug: string;
  name: string;
}

/** Identity-login response — polymorphic by `status` (ADR-0077 S2). */
export interface IdentityLoginResult {
  status: "ok" | "totp_required";
  token?: string | null;
  email?: string | null;
  full_name?: string | null;
  workspaces?: IdentityWorkspace[] | null;
  challenge_token?: string | null;
}

/** The identity profile + memberships (the account page / picker). */
export interface IdentityMe {
  email: string;
  full_name: string | null;
  email_verified: boolean;
  totp_enabled: boolean;
  workspaces: IdentityWorkspace[];
}

/** Pay-then-provision checkout (ADR-0041 Fase 1) — the hosted Stripe URL + a poll token. */
/** One workspace under an organization, in the Tray Account. */
export interface IdentityOrgWorkspace {
  tenant_id: string;
  slug: string;
  name: string;
  status: string;
  plan_name?: string | null;
  subscription_status?: string | null;
  /** Does the caller hold the WORKSPACE arc? An org admin routinely administers
   * workspaces they cannot enter — so the UI must not offer a door that would refuse. */
  can_enter: boolean;
}

export interface IdentityOrganization {
  id: string;
  name: string;
  status: string;
  /** `admin` may act ON the company; `member` just belongs to it. */
  role: string;
  workspaces: IdentityOrgWorkspace[];
}

export interface IdentityOrganizations {
  organizations: IdentityOrganization[];
  /** Workspaces under no organization of mine — the guest case (ADR-0078 slice 3). */
  other_workspaces: IdentityOrgWorkspace[];
}

export interface CheckoutResult {
  checkout_url: string;
  token: string;
}

/** Status of a parked checkout. `redirect_url` is set once `completed`. */
export interface CheckoutStatusResult {
  status: "pending" | "completed" | "expired" | "canceled" | "not_found";
  redirect_url?: string | null;
}

export const onboardingApi = {
  checkSlug: (slug: string) => request<SlugCheck>(`/check-slug?slug=${encodeURIComponent(slug)}`),
  // The ONLY self-serve way in (ADR-0079): park the signup + open a Stripe Checkout
  // Session (returns its hosted URL). The free `/signup` door is gone — a workspace is
  // never created without a plan and its payment.
  checkout: (body: SignupBody) =>
    request<CheckoutResult>("/checkout", { method: "POST", body: JSON.stringify(body) }),
  checkoutStatus: (token: string) =>
    request<CheckoutStatusResult>(`/checkout/status?token=${encodeURIComponent(token)}`),
  plans: () => request<OnboardingPlan[]>("/plans"),
};

// ---------------------------------------------------------------------------
// Account Tray identity (ADR-0077 S2) — login, picker handoff, account, reset.
// The identity session token lives in localStorage on the `app.<base>` origin
// (like every other realm's token store); it never authenticates a tenant host.
// ---------------------------------------------------------------------------

const IDENTITY_TOKEN_KEY = "tray:identity:token";

export function identityToken(): string | null {
  try {
    return localStorage.getItem(IDENTITY_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setIdentityToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(IDENTITY_TOKEN_KEY, token);
    else localStorage.removeItem(IDENTITY_TOKEN_KEY);
  } catch {
    /* storage unavailable — session just won't persist */
  }
}

function authed(init: RequestInit = {}): RequestInit {
  const token = identityToken();
  return {
    ...init,
    headers: { ...(init.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  };
}

export const identityApi = {
  login: (body: { email: string; password: string }) =>
    request<IdentityLoginResult>("/login", { method: "POST", body: JSON.stringify(body) }),
  loginTotp: (body: { challenge_token: string; code: string }) =>
    request<IdentityLoginResult>("/login/totp", { method: "POST", body: JSON.stringify(body) }),
  me: () => request<IdentityMe>("/me", authed()),
  organizations: () => request<IdentityOrganizations>("/organizations", authed()),
  createOrganization: (name: string) =>
    request<IdentityOrganization>(
      "/organizations",
      authed({ method: "POST", body: JSON.stringify({ name }) }),
    ),
  // Buying a workspace is an ORG power: the backend gates it on the org-admin arc,
  // so this 422s with `organization:not_org_admin` for a mere member (ADR-0078 s4).
  // …and it is a PURCHASE (ADR-0079): `plan_key` is required and what comes back is a
  // Stripe Checkout URL, not a workspace — nothing exists until the payment clears.
  createOrgWorkspace: (
    organizationId: string,
    // `billing` is the NEW workspace's own invoicing profile and is REQUIRED in
    // practice (owner, 2026-07-28): the backend refuses an incomplete one with
    // `billing:incomplete:<fields>`. Typed optional only because the wire field is.
    body: {
      slug: string;
      name?: string;
      plan_key: string;
      billing_period?: "month" | "year";
      billing?: BillingPayload | null;
    },
  ) =>
    request<{ slug: string; checkout_url: string; token: string }>(
      `/organizations/${organizationId}/workspaces`,
      authed({ method: "POST", body: JSON.stringify(body) }),
    ),
  patchMe: (body: { full_name: string | null }) =>
    request<IdentityMe>("/me", authed({ method: "PATCH", body: JSON.stringify(body) })),
  // Enter a workspace: the handoff rides a parent-domain HttpOnly cookie; the
  // returned redirect carries only the `?handoff=1` flag.
  handoff: (slug: string) =>
    request<{ redirect_url: string }>("/handoff", {
      ...authed({ method: "POST", body: JSON.stringify({ slug }) }),
      credentials: "include",
    }),
  // Enter Tray Central as THIS identity (SSO, 2026-08-01): same cookie mechanics as
  // the workspace handoff. Central retired its own login form — `?continue=central`
  // routes here instead of the workspace picker.
  centralHandoff: () =>
    request<{ redirect_url: string }>("/central-handoff", {
      ...authed({ method: "POST", body: "{}" }),
      credentials: "include",
    }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    request<{ token: string }>("/change-password", {
      ...authed({ method: "POST", body: JSON.stringify(body) }),
    }),
  totpBegin: () =>
    request<{ secret: string; otpauth_uri: string }>(
      "/2fa/begin",
      authed({ method: "POST", body: "{}" }),
    ),
  totpConfirm: (code: string) =>
    request<{ recovery_codes: string[] }>("/2fa/confirm", {
      ...authed({ method: "POST", body: JSON.stringify({ code }) }),
    }),
  totpDisable: (code: string) =>
    request<{ ok: boolean }>("/2fa/disable", {
      ...authed({ method: "POST", body: JSON.stringify({ code }) }),
    }),
  resetRequest: (email: string) =>
    request<{ delivery: "sent" | "unavailable" }>("/reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetConfirm: (body: { token: string; password: string }) =>
    request<{ token: string }>("/reset/confirm", { method: "POST", body: JSON.stringify(body) }),
};

/** The base domain under the onboarding host (`app.trayos.dev` → `trayos.dev`),
 * shown as the slug suffix. Falls back to a sensible default off-host (dev path
 * preview / tests). */
export function onboardingBaseDomain(): string {
  if (typeof window === "undefined") return "trayos.dev";
  const parts = window.location.hostname.split(".");
  const rest = parts.slice(1).join(".");
  return rest || parts.join(".") || "trayos.dev";
}
